import { Component, OnInit } from '@angular/core';
import {
  RegionDecisionRulesService,
  RegionDecisionRule,
  RuleState,
  FacilityState,
} from './region-decision-rules.service';
import { UtilsService } from '../../manageb/utils.service';
import { FacilityService } from '../../manageb/facility.service';
import { FacilityEntry } from 'list-types-public';

export type SegmentState = 'decline' | 'review' | 'na';

export interface Rule {
  name: string;
  states: SegmentState[];
  masterState: SegmentState;
}

export interface LogicCategory {
  key: string;
  title: string;
  desc: string;
  rules: Rule[];
}

export interface FacilityCol {
  id: string;
  displayName: string;
}

function rowFromApi(
  name: string,
  facilityIds: string[],
  rule?: RegionDecisionRule,
): Rule {
  const states: SegmentState[] = facilityIds.map((id) => {
    if (!rule) return 'na';
    const found = rule.facilityStates.find(
      (fs) => String(fs.facilityId) === String(id),
    );
    return (found?.state as SegmentState) ?? 'na';
  });
  const masterState: SegmentState =
    states.length > 0 && states.every((s) => s === states[0]) ? states[0] : 'na';
  return { name, states, masterState };
}

@Component({
  selector: 'ngx-ai-decision-logic',
  templateUrl: './ai-decision-logic.component.html',
  styleUrls: ['./ai-decision-logic.component.scss'],
})
export class AiDecisionLogicComponent implements OnInit {
  facilities: FacilityCol[] = [];
  categories: Record<string, LogicCategory> = {
    care: {
      key: 'care',
      title: 'Care Requirements',
      desc: 'Special medical needs, isolations, and clinical procedures.',
      rules: [],
    },
    behavioral: {
      key: 'behavioral',
      title: 'Behavioral & Mental Health',
      desc: 'Risk assessments for psychiatric and behavioral cases.',
      rules: [],
    },
  };

  subTabs: { key: string; label: string }[] = [
    { key: 'care', label: 'Care Requirements' },
    { key: 'behavioral', label: 'Behavioral & Mental Health' },
  ];

  currentSubTab: string = 'care';
  aiDecisionLogicEnabled = false;
  aiDecisionLogicSaving = false;
  loading = false;
  downloading = false;
  loadError: string | null = null;
  saveMessage: string | null = null;
  downloadError: string | null = null;

    trackByRuleIndex( index: number): number {
    return index;
  }

  trackByFacilityIndex( index: number): number {
    return index;
  }

  constructor(
    private rulesService: RegionDecisionRulesService,
    private utils: UtilsService,
    private facilityService: FacilityService,
  ) {}

  get currentCategory(): LogicCategory {
    return this.categories[this.currentSubTab];
  }

  ngOnInit(): void {
    this.aiDecisionLogicEnabled = this.utils.isAiDecisionLogicEnabled();
    this.loadAll();
  }

  // Persist-only: toggling never gates the grid below — rules stay editable and
  // their saves take effect once the client is enabled. Optimistic; reverts on error.
  toggleAiDecisionLogic(): void {
    if (this.aiDecisionLogicSaving) return;
    const next = !this.aiDecisionLogicEnabled;
    this.aiDecisionLogicEnabled = next;
    this.aiDecisionLogicSaving = true;
    this.saveMessage = null;
    this.rulesService.setAiDecisionLogicEnabled(next).subscribe({
      next: () => {
        // The cache must reflect the save or the referral screen keeps showing
        // "not configured" until a full reload. Patch through the service so
        // `settings$` emits — assigning to `clientSettings.uiSettings` would
        // mutate the cached object in place and notify nobody.
        this.utils.patchUiSettings({ aiDecisionLogicEnabled: next });
        this.aiDecisionLogicSaving = false;
      },
      error: () => {
        this.aiDecisionLogicEnabled = !next;
        this.aiDecisionLogicSaving = false;
        this.saveMessage = 'Failed to save AI decision logic setting.';
      },
    });
  }

  private buildFacilityColsFromUtils(sites: FacilityEntry[]): FacilityCol[] {
    if (!sites || Object.keys(sites).length === 0) {
      return [];
    }
    return sites
      .sort((a, b) => a.id - b.id)
      .map(({id, displayName}) => ({
        id: String(id),
        displayName,
      }));
  }

  private applyRulesToCategory(
    key: 'care' | 'behavioral',
    rules: RegionDecisionRule[],
    facilityIds: string[],
  ): void {
    this.categories[key].rules = rules.map((rule) =>
      rowFromApi(rule.clientCategoryName, facilityIds, rule),
    );
  }

  private loadAll(): void {
    this.loading = true;
    this.loadError = null;
    let sites = this.facilityService.getUserFacilities();
    if (!sites || Object.keys(sites).length === 0) {
      this.utils.refreshSettings().then(() => {
        sites = this.facilityService.getUserFacilities();
        this.facilities = this.buildFacilityColsFromUtils(sites);
        this.fetchRules();
      });
    } else {
      this.facilities = this.buildFacilityColsFromUtils(sites);
      this.fetchRules();
    }
  }

  private fetchRules(): void {
    const facilityIds = this.facilities.map((f) => f.id);
    this.rulesService.getRulesAll().subscribe({
      next: ({ care: careRules, behavioral: behavioralRules }) => {
        this.applyRulesToCategory('care', careRules, facilityIds);
        this.applyRulesToCategory('behavioral', behavioralRules, facilityIds);
        this.loading = false;
      },
      error: (err) => {
        this.loadError = err?.message || 'Failed to load rules';
        this.loading = false;
      },
    });
  }

  selectSubTab(key: string): void {
    this.currentSubTab = key;
  }

  setSegment(ruleIndex: number, facilityIndex: number, state: SegmentState): void {
    const category = this.currentCategory;
    if (!category || ruleIndex < 0 || ruleIndex >= category.rules.length) return;
    const rule = category.rules[ruleIndex];
    rule.states[facilityIndex] = state;
    // When any facility state changes, Apply to All shows N/A
    rule.masterState ='na';
    this.saveRow(ruleIndex);
  }

  setSegmentAll(rowIndex: number, state: SegmentState): void {
    const cat = this.currentCategory;
    if (!cat || rowIndex < 0 || rowIndex >= cat.rules.length) return;
    const r = cat.rules[rowIndex];
    r.masterState = state;
    r.states.fill(state);
    this.saveRow(rowIndex);
  }

  private saveRow(rowIndex: number): void {
    const category = this.currentCategory;
    const logicCategory = this.currentSubTab as 'care' | 'behavioral';
    if (
      !category.rules.length ||
      !this.facilities.length ||
      rowIndex < 0 ||
      rowIndex >= category.rules.length
    )
      return;
    const rule = category.rules[rowIndex];
    const facilityStates: FacilityState[] = this.facilities.map((f, i) => ({
      facilityId: f.id,
      state: rule.states[i] as RuleState,
    }));
    this.saveMessage = null;
    this.rulesService
      .saveRule({
        logicCategory,
        clientCategoryName: rule.name,
        facilityStates,
      })
      .subscribe({
        next: () => {
          this.saveMessage = 'Saved.';
          setTimeout(() => (this.saveMessage = null), 2000);
        },
        error: () => {
          this.saveMessage = 'Failed to save.';
        },
      });
  }

  private getDownloadFilenameJson(): string {
    const date = new Date().toISOString().slice(0, 10);
    return `AI_Decision_Logic_${date}.json`;
  }
}
